import { create } from 'zustand'

import { checkTabFullyComplete } from '@/lib/utils'
import {
  COMPANY_PAGE_OVERVIEW_WORKFLOWS,
  COMPANY_PAGE_INDUSTRY_WORKFLOWS,
  COMPANY_PAGE_EARNINGS_HISTORY_WORKFLOWS,
  COMPANY_PAGE_OWNERSHIP_WORKFLOWS,
  COMPANY_PAGE_RECENT_INFO_WORKFLOWS,
  COMPANY_PAGE_FINANCIALS_WORKFLOWS,
} from '@/constants/company_page'
import { QuarterInfo } from '@/lib/StockChart/stockChartUtils'

export enum CompanyPageTab {
  RecentInfo = 'recent_info',
  Overview = 'overview',
  Financials = 'financials',
  Industry = 'industry',
  Ownership = 'ownership',
  EarningsHistory = 'earnings_history',
}

// 캐싱 없이 즉시 완료되는 탭들 - 이제 없음
export const INSTANT_COMPLETE_TABS: CompanyPageTab[] = []

type TabCompletionState = Partial<Record<CompanyPageTab, boolean>>

interface CompanyPageState {
  activeTab: CompanyPageTab
  setActiveTab: (tab: CompanyPageTab) => void
  tabCompletionStatus: TabCompletionState
  setTabCompletionStatus: (status: TabCompletionState) => void
  markTabAsViewed: (messageId: string, tab: CompanyPageTab) => void
  isTabUnread: (messageId: string, tab: CompanyPageTab) => boolean
  getCompletedTabsCount: (batchResults: any) => number
  selectedEarningsQuarter: Record<string, QuarterInfo | null>
  setSelectedEarningsQuarter: (messageId: string, quarter: QuarterInfo | null) => void
  // 패널 자동 오픈을 위한 상태 (null이면 열지 않음, string이면 해당 message ID의 패널을 열어야 함)
  shouldOpenPanel: string | null
  setShouldOpenPanel: (messageId: string | null) => void
  // Language for company page content generation (ISO 639-1 code, 'en' = English)
  lang: string
  setLang: (lang: string) => void
}

export const useCompanyPageStore = create<CompanyPageState>((set) => ({
  activeTab: CompanyPageTab.RecentInfo,
  tabCompletionStatus: {
    [CompanyPageTab.RecentInfo]: true,
  },
  selectedEarningsQuarter: {},
  shouldOpenPanel: null,
  lang: typeof window !== 'undefined' ? localStorage.getItem('company_page_lang') || 'en' : 'en',

  setShouldOpenPanel: (messageId) => set({ shouldOpenPanel: messageId }),
  setLang: (lang) => {
    if (typeof window !== 'undefined') localStorage.setItem('company_page_lang', lang)
    set({ lang })
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  setTabCompletionStatus: (status) => set({ tabCompletionStatus: status }),

  setSelectedEarningsQuarter: (messageId: string, quarter: QuarterInfo | null) =>
    set((state) => ({
      selectedEarningsQuarter: {
        ...state.selectedEarningsQuarter,
        [messageId]: quarter,
      },
    })),

  markTabAsViewed: (messageId: string, tab: CompanyPageTab) => {
    const key = `company_page_tab_viewed_${messageId}`
    const stored = localStorage.getItem(key)
    const viewedTabs: string[] = stored ? JSON.parse(stored) : []

    if (!viewedTabs.includes(tab)) {
      viewedTabs.push(tab)
      localStorage.setItem(key, JSON.stringify(viewedTabs))
    }
  },

  isTabUnread: (messageId: string, tab: CompanyPageTab) => {
    const key = `company_page_tab_viewed_${messageId}`
    const stored = localStorage.getItem(key)
    if (!stored) return true

    const viewedTabs: string[] = JSON.parse(stored)
    return !viewedTabs.includes(tab)
  },

  getCompletedTabsCount: (batchResults: any) => {
    let count = 0

    // 각 탭의 모든 워크플로우가 완료되었는지 체크
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_RECENT_INFO_WORKFLOWS)) count++
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_OVERVIEW_WORKFLOWS)) count++
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_FINANCIALS_WORKFLOWS)) count++
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_INDUSTRY_WORKFLOWS)) count++
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_OWNERSHIP_WORKFLOWS)) count++
    if (checkTabFullyComplete(batchResults, COMPANY_PAGE_EARNINGS_HISTORY_WORKFLOWS)) count++

    return count
  },
}))
